import React from 'react'

import { Sanitizer } from '@esri/arcgis-html-sanitizer'
import linkifyString from 'linkify-string'

// The following custom protocols are allowed to be used in URLs
export const allowedCustomProtocols = {
  // Civica Authority
  'aul:': {
    domain: 'civica.com'
  },

  // Kapish Easy Link
  'el:': {
    domain: 'kapish.com.au'
  },

  // Assetic / Brightly
  'mydata:': {
    domain: 'brightlysoftware.com'
  },

  // Infor Pathway
  'pathway:': {
    domain: 'infor.com'
  }
}

const sanitzerOptions: XSS.IFilterXSSOptions = {
  whiteList: {
    a: ['href', 'title'],
    iframe: ['src', 'width', 'height', 'frameborder']
  },

  onTagAttr: function (tag, name, value) {
    // // console.debug('sanitizerOptions - onTagAttr', tag, name, value)

    // Add target="_blank" and rel="noopener" to the <a> tag
    if (tag === 'a' && name === 'href') {
      return `href="${encodeURI(value)}" rel="${linkifyOptions.rel}" target="${linkifyOptions.target}"`
    }

    // Only allow iframes from https://www.mapillary.com/embed
    if (tag === 'iframe' && name === 'src') {
      const srcIsValid = value.startsWith('https://www.mapillary.com/embed')
      if (!srcIsValid) {
        const message = `Invalid URL in iframe ('${value}'). Please consult your GIS administrator.`

        return `src=data:text/plain;charset=utf-8,${encodeURIComponent(message)}`
      }
    }
  }
}

const sanitizer = new Sanitizer(sanitzerOptions, true)

// Add all custom allowed protocols to the sanitizer
const protocols = Object.keys(allowedCustomProtocols)
for (let protocol of protocols) {
  protocol = protocol.replace(':', '') // Remove the colon
  sanitizer.allowedProtocols.push(protocol)
}

// Regular expression for matching hash keys and their values.
// We only accept key values that are letters (i.e. no numbers or other characters are not allowed).
// It allows for (accidental) whitespace between the key and the value.
// E.g. 'feature[whatshere,MULTIPOINT((144.8 -37.7),(144.9 -37.8))]' => ['feature', 'whatshere,MULTIPOINT((144.8 -37.7),(144.9 -37.8))']
// export const hashItemRegExp = /^([a-zA-Z]+)\W*\[(.*)\]$/
export const hashItemRegExp = /^([a-zA-Z_]+)\W*(?:\[|%5B)(.*)(?:\]|%5D)$/ // this handles URI encoded brackets

// Create an object from a hash item and its value(s).
// E.g. 'feature[whatshere,MULTIPOINT((144.8 -37.7),(144.9 -37.8))]'
//        =>
//        { key: 'feature', values: ['whatshere', 'MULTIPOINT((144.8 -37.7),(144.9 -37.8))'] }
export const parseHashItem = (hashItem = '') => {
  const match = hashItem.trim().match(hashItemRegExp)

  // Ignore items that don't have a match
  if (!match) {
    return null
  }

  // From the example above, this would result in:
  // key: 'feature'
  // value: 'whatshere,MULTIPOINT((144.8 -37.7),(144.9 -37.8))'

  let [, key, value] = match

  // All keys are lowercase
  key = key.toLowerCase()

  // The value field can be a single or double value (more than 2 values are currently not allowed in Pozi),
  // so we need to split the value string into an array. We split by the first comma and prevent the last match
  // from being an empty string (this is a side effect from the regular expression used to split by comma).
  // const valuesArr = value ? value.split(/,(.+)/, 2) : []

  // The value field can be a single or multiple values, so we need to split the value string into an array
  let values = value ? value.split(',') : []

  // URI decode and trim the values
  values = values.map(str => decodeURIComponent(str).trim())

  // console.debug('parseHashItem - hash item key/values', key, values)

  const parsedHashItem = {
    key,
    values
  }

  return parsedHashItem
}

export const parseHash = (hashStr = '') => {
  const parsedHashItems = []

  // Split the hash string by '/' to get an array of hash items
  const hashArr = hashStr.split('/')

  // console.debug('parseHash - hashArr', hashArr)

  for (const item of hashArr) {
    const parsedItem = parseHashItem(item)

    if (!parsedItem) {
      continue
    }

    // console.debug('parseHash - parsedItem', parsedItem)

    parsedHashItems.push(parsedItem)
  }

  // console.debug('setStateFromHash - parsedHashItems', parsedHashItems)

  return parsedHashItems
}

const linkifyOptions = {
  attributes: {
    title: 'This link opens in a new tab/window'
  },
  target: '_blank',
  rel: 'noopener noreferrer'
}

export const linkifyText = (text: string) => {
  const linkifiedValue = linkifyString(text, linkifyOptions)
  if (!linkifiedValue || linkifiedValue === text) {
    return text
  }

  // console.debug('linkifyText', linkifiedValue)

  return (<span dangerouslySetInnerHTML={{ __html: linkifiedValue }} />)
}

// Make HTML and URL sanitisers available
export const sanitiseHtml = (html: string) => sanitizer.sanitize(html)
export const sanitiseUrl = (url: string) => sanitizer.sanitizeUrl(url)
